import React from 'react';
import { CheckCircle2, Shield, Award, Clock, Users } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '../components/ui/accordion';

export const ServiceTemplate = ({ service, onOpenPopup, showPricing = false, PricingComponent }) => {
  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-orange-600 via-orange-500 to-red-600 text-white py-20 overflow-hidden">
        {/* Background Image */}
        <div className="absolute inset-0 z-0">
          <img
            src="https://images.unsplash.com/photo-1718220216044-006f43e3a9b1?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1ODF8MHwxfHNlYXJjaHwyfHxwcm9mZXNzaW9uYWwlMjBvZmZpY2V8ZW58MHx8fHwxNzY0MzYzNjc1fDA&ixlib=rb-4.1.0&q=85"
            alt="Professional workspace background"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-br from-orange-600/75 via-orange-500/75 to-red-600/80"></div>
        </div>
        
        <div className="container mx-auto px-4 relative z-10">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-5xl md:text-6xl font-bold mb-6">{service.title}</h1>
            <p className="text-xl md:text-2xl text-orange-100 mb-8">
              {service.description}
            </p>
            <Button
              size="lg"
              onClick={onOpenPopup}
              className="bg-white text-orange-600 hover:bg-orange-50 text-lg px-8 py-6 font-semibold"
            >
              Get Started Now
            </Button>
          </div>
        </div>
      </section>

      {/* Overview Section */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="grid lg:grid-cols-2 gap-12 items-center max-w-6xl mx-auto">
            {/* Content - Left Side */}
            <div className="w-full">
              <h2 className="text-4xl font-bold text-gray-900 mb-6">
                {service.title}: Your Complete Solution
              </h2>
              <div className="text-lg text-gray-700 leading-relaxed space-y-4">
                {service.overview.map((paragraph, index) => (
                  <p key={index}>{paragraph}</p>
                ))}
              </div>
              <div className="mt-8">
                <Button
                  onClick={onOpenPopup}
                  className="bg-orange-600 hover:bg-orange-700 text-white px-8 py-4 text-lg font-semibold"
                >
                  Get Started Today
                </Button>
              </div>
            </div>

            {/* Image - Right Side */}
            <div className="w-full">
              <img
                src="https://images.unsplash.com/photo-1450101499163-c8848c66ca85?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2Mzl8MHwxfHNlYXJjaHw1fHxidXNpbmVzcyUyMGRvY3VtZW50c3xlbnwwfHx8fDE3NjQzNzY0MjF8MA&ixlib=rb-4.1.0&q=85"
                alt={`${service.title} professional services`}
                className="w-full h-auto rounded-2xl shadow-2xl"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Why Choose Us Section */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">Why Choose Corporate Marking?</h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              We provide comprehensive trademark services tailored to protect your business
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8 max-w-6xl mx-auto">
            <Card className="p-6 text-center hover:shadow-xl transition-shadow">
              <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-orange-100 text-orange-600 mb-4">
                <Shield className="h-8 w-8" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">Comprehensive Protection</h3>
              <p className="text-gray-600">
                Full-service trademark protection with ongoing monitoring and support
              </p>
            </Card>

            <Card className="p-6 text-center hover:shadow-xl transition-shadow">
              <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-orange-100 text-orange-600 mb-4">
                <Award className="h-8 w-8" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">Expert Legal Team</h3>
              <p className="text-gray-600">
                Experienced attorneys specialized in intellectual property law
              </p>
            </Card>

            <Card className="p-6 text-center hover:shadow-xl transition-shadow">
              <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-orange-100 text-orange-600 mb-4">
                <Clock className="h-8 w-8" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">Fast Processing</h3>
              <p className="text-gray-600">
                Quick turnaround times with expedited filing options available
              </p>
            </Card>

            <Card className="p-6 text-center hover:shadow-xl transition-shadow">
              <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-orange-100 text-orange-600 mb-4">
                <Users className="h-8 w-8" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">Dedicated Support</h3>
              <p className="text-gray-600">
                Personal case manager assigned to guide you through every step
              </p>
            </Card>
          </div>
        </div>
      </section>

      {/* Quick 3-Step Process Section */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">How It Works</h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              Get started with our simple 3-step process
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8 max-w-5xl mx-auto">
            <Card className="p-8 text-center hover:shadow-xl transition-all relative">
              <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-gradient-to-br from-orange-500 to-red-500 text-white mb-6">
                <span className="text-3xl font-bold">1</span>
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">Consultation</h3>
              <p className="text-gray-600 leading-relaxed">
                Schedule a free consultation with our expert team to discuss your needs and determine the best strategy for your brand protection.
              </p>
            </Card>

            <Card className="p-8 text-center hover:shadow-xl transition-all relative">
              <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-gradient-to-br from-orange-500 to-red-500 text-white mb-6">
                <span className="text-3xl font-bold">2</span>
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">Preparation</h3>
              <p className="text-gray-600 leading-relaxed">
                We handle all paperwork, conduct comprehensive searches, and prepare your application with meticulous attention to detail.
              </p>
            </Card>

            <Card className="p-8 text-center hover:shadow-xl transition-all relative">
              <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-gradient-to-br from-orange-500 to-red-500 text-white mb-6">
                <span className="text-3xl font-bold">3</span>
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">Protection</h3>
              <p className="text-gray-600 leading-relaxed">
                Your application is filed and monitored until completion. We keep you informed every step of the way and handle any issues that arise.
              </p>
            </Card>
          </div>

          <div className="text-center mt-12">
            <Button
              onClick={onOpenPopup}
              size="lg"
              className="bg-orange-600 hover:bg-orange-700 text-white px-10 py-6 text-lg font-semibold"
            >
              Start Your Application Now
            </Button>
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">Key Benefits</h2>
            <p className="text-xl text-gray-600">Why this service is essential for your business</p>
          </div>

          <div className="grid md:grid-cols-2 gap-6 max-w-4xl mx-auto">
            {service.benefits.map((benefit, index) => (
              <Card key={index} className="p-6 hover:shadow-lg transition-shadow">
                <div className="flex items-start space-x-4">
                  <div className="flex-shrink-0">
                    <CheckCircle2 className="h-6 w-6 text-green-600" />
                  </div>
                  <div>
                    <h3 className="font-bold text-gray-900 mb-2">{benefit.title}</h3>
                    <p className="text-gray-600">{benefit.description}</p>
                  </div>
                </div>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing Section - Only for Trademark Registration */}
      {showPricing && PricingComponent && <PricingComponent onOpenPopup={onOpenPopup} />}

      {/* FAQ Section */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="grid lg:grid-cols-2 gap-12 items-start max-w-6xl mx-auto">
            {/* Left Side - FAQ Content */}
            <div className="order-2 lg:order-1 w-full">
              <h2 className="text-4xl font-bold text-gray-900 mb-6">
                Frequently Asked Questions
              </h2>
              <p className="text-lg text-gray-600 mb-8">
                Get answers to common questions about {service.title.toLowerCase()}
              </p>

              <Accordion type="single" collapsible className="space-y-4">
                {service.faqs.map((faq, index) => (
                  <AccordionItem key={index} value={`item-${index}`} className="bg-white border border-gray-200 rounded-lg px-6">
                    <AccordionTrigger className="text-left font-semibold text-gray-900 hover:text-orange-600">
                      <div className="flex items-center space-x-3">
                        <span className="w-8 h-8 rounded-full bg-orange-100 text-orange-600 flex items-center justify-center text-sm font-bold flex-shrink-0">Q</span>
                        <span>{faq.question}</span>
                      </div>
                    </AccordionTrigger>
                    <AccordionContent className="text-gray-600 pl-11 pt-2">
                      {faq.answer}
                    </AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>

              <div className="mt-8">
                <Button
                  onClick={onOpenPopup}
                  className="bg-orange-600 hover:bg-orange-700 text-white px-8 py-6 text-lg font-semibold"
                >
                  Have More Questions? Contact Us
                </Button>
              </div>
            </div>

            {/* Right Side - Image */}
            <div className="order-1 lg:order-2 w-full">
              <img
                src="https://images.unsplash.com/photo-1551836022-d5d88e9218df?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Njd8MHwxfHNlYXJjaHwyfHxidXNpbmVzcyUyMGNvbnN1bHRhdGlvbnxlbnwwfHx8fDE3NjQzNjYxNjF8MA&ixlib=rb-4.1.0&q=85"
                alt="Professional consultation and support"
                className="w-full h-full object-cover rounded-2xl shadow-2xl min-h-[600px]"
              />
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-r from-orange-600 to-red-600 text-white">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-4xl font-bold mb-6">Ready to Get Started?</h2>
          <p className="text-xl mb-8 text-orange-100 max-w-2xl mx-auto">
            Protect your brand today with our expert {service.title.toLowerCase()} services
          </p>
          <Button
            size="lg"
            onClick={onOpenPopup}
            className="bg-white text-orange-600 hover:bg-orange-50 text-lg px-8 py-6 font-semibold"
          >
            Start Your Application
          </Button>
        </div>
      </section>
    </div>
  );
};
